/**
 * The one versioned release-configuration register — PRD §14.6, §14.3, §14.2, §14.9,
 * Part Three Addenda C and D (register §8).
 *
 * Rules (00-conventions.md, "Config register rule"):
 * - Every entry carries its `source` and a `status`.
 * - `FIXED` values are commercial/confirmed and must not drift.
 * - `CALIBRATION`/`GATE`/`REVIEW` values are authoritative starting points that
 *   require a recorded readiness result before beta; a `null` value means the
 *   parameter is still unset and callers must fail closed (never assume unlimited).
 * - §14.9 `V17-CFG-01..11` rows **supplement** §14.6 without renumbering it; they are
 *   `GATE` rows whose `null` value means the gated part is still undecided.
 * - Adopted Addendum C/D defaults, limits and targets (register §8) enter as
 *   `GATE`/`CALIBRATION` rows carrying their C/D source IDs: approved values, not
 *   achieved results, until a recorded measurement exists.
 * - Changing a value updates dependent tests and disclosures in the same change.
 */

export type ConfigStatus = 'FIXED' | 'CALIBRATION' | 'GATE' | 'REVIEW'

export interface ReleaseConfigEntry<T = unknown> {
  readonly key: string
  readonly value: T
  readonly unit: string
  readonly status: ConfigStatus
  readonly source: string
  readonly note?: string
}

function entry<T>(
  key: string,
  value: T,
  unit: string,
  status: ConfigStatus,
  source: string,
  note?: string,
): ReleaseConfigEntry<T> {
  return note === undefined
    ? { key, value, unit, status, source }
    : { key, value, unit, status, source, note }
}

export const RELEASE_CONFIG = {
  /* --------------------------------------------------------------------- */
  /* Confirmed commercial limits — FIXED                                   */
  /* --------------------------------------------------------------------- */
  free_external_signers: entry(
    'free_external_signers',
    1,
    'signers',
    'FIXED',
    'PRD §14.6 (Free signer/active/expiry limits); §2.2',
    'Sender who also signs does not count toward the external limit.',
  ),
  free_active_requests: entry(
    'free_active_requests',
    3,
    'requests',
    'FIXED',
    'PRD §14.6 (Free signer/active/expiry limits); §2.2',
    'Terminal requests release their slot.',
  ),
  free_signing_days: entry(
    'free_signing_days',
    7,
    'days',
    'FIXED',
    'PRD §14.6 (Free signer/active/expiry limits); §6.5',
    'Expiry is activated_at + 7 elapsed days (604800000 ms); sequential steps share it.',
  ),
  lifetime_external_signers: entry(
    'lifetime_external_signers',
    5,
    'signers',
    'FIXED',
    'PRD §14.6 (Lifetime signer/active limits); §2.2',
  ),
  lifetime_active_requests: entry(
    'lifetime_active_requests',
    25,
    'requests',
    'FIXED',
    'PRD §14.6 (Lifetime signer/active limits); §2.2',
    'Active + finalizing + unexpired preparation reservations count; reservations shown separately.',
  ),
  lifetime_default_signing_days: entry(
    'lifetime_default_signing_days',
    30,
    'days',
    'FIXED',
    'PRD §14.6 (Lifetime default duration); §6.1',
  ),
  lifetime_max_signing_days: entry(
    'lifetime_max_signing_days',
    90,
    'days',
    'FIXED',
    'PRD §14.6 (Lifetime maximum duration); §6.5',
    'The single reviewed extension may only lengthen to ≤90 days from original activation.',
  ),
  lifetime_credit_grant: entry(
    'lifetime_credit_grant',
    100,
    'credits',
    'FIXED',
    'PRD §14.6 (Included Lifetime grant); §2.5; D05',
    'Non-expiring; once per qualifying purchase cycle. Restore/replay/duplicate mints nothing.',
  ),
  cc_recipient_cap: entry(
    'cc_recipient_cap',
    5,
    'recipients',
    'FIXED',
    'PRD §14.6 (CC cap); §6.1',
    'Lifetime only; final read access after completion, no signing authority or fields.',
  ),
  template_soft_delete_days: entry(
    'template_soft_delete_days',
    30,
    'days',
    'FIXED',
    'PRD §14.6 (Template soft deletion); §14.2',
    'Restore before the exact boundary; purge at it.',
  ),
  final_download_days: entry(
    'final_download_days',
    7,
    'days',
    'FIXED',
    'PRD §14.6 (Final remote download); §14.2',
    'Measured from accepted completion, per authorized party (saved workspace copies are separate).',
  ),
  lifetime_artifact_retention_days: entry(
    'lifetime_artifact_retention_days',
    30,
    'days',
    'FIXED',
    'PRD §14.2 (Completed temporary signing artifacts; expired, declined, voided or finalization-failed requests)',
    "Approved deviation DEV-008 (Hamza, 2026-10-12, D2): a Lifetime request keeps its server copies 30 days after completion or its terminal event, where the PRD says 7. A Free request keeps `final_download_days`. Fixed to the request's tier when it was created.",
  ),
  inactive_account_years: entry(
    'inactive_account_years',
    2,
    'years',
    'FIXED',
    'PRD §14.2 (Account deletion); sign-in-once spec §9 decision 1 (Hamza, 2026-10-13, via the advisor)',
    "Delete after 2 years of inactivity, with 30 days' notice, except accounts with active requests or purchases. The PRD has no inactivity deletion: this is Hamza's decision, part of the sign-in-once deviation draft, and uses the §14.2 account-deletion path.",
  ),
  inactive_account_notice_days: entry(
    'inactive_account_notice_days',
    30,
    'days',
    'FIXED',
    'PRD §14.2 (Account deletion); sign-in-once spec §9 decision 1 (Hamza, 2026-10-13, via the advisor)',
    'The notice period between the inactivity email and the deletion; using the account in it cancels the deletion.',
  ),
  preparation_reservation_minutes: entry(
    'preparation_reservation_minutes',
    15,
    'minutes',
    'FIXED',
    'PRD §14.6 (Preparation reservation TTL); §2.6',
  ),
  finalization_recovery_days: entry(
    'finalization_recovery_days',
    7,
    'days',
    'FIXED',
    'PRD §14.6 (Finalization recovery); §6.6',
    'Measured from the last required accepted response; timeout enters FINALIZATION_FAILED.',
  ),
  finalization_lease_seconds: entry(
    'finalization_lease_seconds',
    120,
    'seconds',
    'FIXED',
    'PRD §14.6 (Finalization lease); §6.7',
    'Renewable while progressing within the recovery deadline; stale owners are fenced.',
  ),
  attention_expiry_window_hours: entry(
    'attention_expiry_window_hours',
    24,
    'hours',
    'FIXED',
    'PRD §14.6 (Attention expiry window); §3.7',
    'Only actionable signing deadlines; sealed finalization sets use their own recovery deadline.',
  ),
  incomplete_evidence_export_days: entry(
    'incomplete_evidence_export_days',
    7,
    'days',
    'FIXED',
    'PRD §14.2 (Expired, declined, voided or finalization-failed requests)',
    'How long after a request ends unsigned the sender may save its clearly labelled activity record.',
  ),
  attention_declined_days: entry(
    'attention_declined_days',
    7,
    'days',
    'FIXED',
    'PRD §6.5 (Decline: notify sender status), §3.7; flow-gaps decision (Hamza, 2026-10-16)',
    'How long a declined request stays in Needs attention so the sender sees the reason and can send a new request.',
  ),
  refresh_focus_coalesce_seconds: entry(
    'refresh_focus_coalesce_seconds',
    30,
    'seconds',
    'FIXED',
    'PRD §14.6 (Refresh behavior); §9.3',
  ),
  manual_refresh_min_interval_seconds: entry(
    'manual_refresh_min_interval_seconds',
    5,
    'seconds',
    'FIXED',
    'PRD §14.6 (Refresh behavior); §9.3',
  ),
  upload_object_grant_minutes: entry(
    'upload_object_grant_minutes',
    5,
    'minutes',
    'FIXED',
    'PRD §14.6 (Grants and capability duration); §7.5',
  ),
  download_object_grant_seconds: entry(
    'download_object_grant_seconds',
    60,
    'seconds',
    'FIXED',
    'PRD §14.6 (Grants and capability duration); §7.5',
  ),
  signing_grant_authority: entry(
    'signing_grant_authority',
    'until_submission_correction_terminal_or_expiry',
    'policy',
    'FIXED',
    'PRD §14.6 (Grants and capability duration); §7.5',
    'Both upload/download grants remain bounded by the underlying authority.',
  ),
  timing_sample_min_n: entry(
    'timing_sample_min_n',
    5,
    'samples',
    'FIXED',
    'PRD §14.6 (Timing sample threshold); §9.2',
    'Applies to R11/R12 only. R10 with zero denominator is null; R13 known-zero is 0.',
  ),
  metric_rate_decimals: entry(
    'metric_rate_decimals',
    1,
    'decimals',
    'FIXED',
    'PRD §9 global rules; §14.6 (Timing sample threshold)',
    'Percentages use one documented decimal place.',
  ),

  /* --------------------------------------------------------------------- */
  /* Purchase catalogue — §2.5; candidate SKUs are gated                    */
  /* --------------------------------------------------------------------- */
  lifetime_product: entry(
    'lifetime_product',
    { product_id: 'signbento_lifetime', price_usd: 4.99, credits: 100, enabled: true },
    'product',
    'FIXED',
    'PRD §2.5; §14.6 (Payment channel matrix)',
    'Permanent feature unlock plus 100 non-expiring delivery credits.',
  ),
  delivery_mini_product: entry(
    'delivery_mini_product',
    { product_id: 'delivery_mini', price_usd: 1.99, credits: 50, enabled: true },
    'product',
    'CALIBRATION',
    'PRD §2.5; §14.6 (Payment channel matrix)',
    'Enabled by product direction on 2026-09-24; delivery/channel margin remains unmeasured and must be reviewed in the operator cost dashboard.',
  ),
  delivery_plus_product: entry(
    'delivery_plus_product',
    { product_id: 'delivery_plus', price_usd: 4.99, credits: 200, enabled: true },
    'product',
    'CALIBRATION',
    'PRD §2.5; §14.6 (Payment channel matrix)',
    'Enabled by product direction on 2026-09-24; delivery/channel margin remains unmeasured and must be reviewed in the operator cost dashboard.',
  ),
  delivery_business_product: entry(
    'delivery_business_product',
    { product_id: 'delivery_business', price_usd: 14.99, credits: 1000, enabled: true },
    'product',
    'GATE',
    'PRD §2.5; §14.6 (Payment channel matrix)',
    'Enabled by product direction on 2026-09-24; this GATE records the outstanding cost and channel-margin review.',
  ),

  /* --------------------------------------------------------------------- */
  /* Delivery budgets and abuse controls — §14.3 (calibrate before beta)    */
  /* --------------------------------------------------------------------- */
  reminders_per_recipient: entry(
    'reminders_per_recipient',
    3,
    'reminders',
    'CALIBRATION',
    'PRD §14.3 (Reminders); §14.6 (Reminders/resends/messages)',
    'Managed automatic and manual reminders combined per pending recipient.',
  ),
  reminder_min_spacing_hours: entry(
    'reminder_min_spacing_hours',
    24,
    'hours',
    'CALIBRATION',
    'PRD §14.3 (Reminders); §14.6',
  ),
  extra_invitations_per_slot: entry(
    'extra_invitations_per_slot',
    2,
    'invitations',
    'CALIBRATION',
    'PRD §14.3 (Resend/correction invitations); §14.6',
    'Correction cannot reset the budget; at least 15 minutes apart.',
  ),
  extra_invitation_min_spacing_minutes: entry(
    'extra_invitation_min_spacing_minutes',
    15,
    'minutes',
    'CALIBRATION',
    'PRD §14.3 (Resend/correction invitations); §14.6',
  ),
  emails_per_envelope: entry(
    'emails_per_envelope',
    50,
    'emails',
    'CALIBRATION',
    'PRD §14.3 (Logical message budgets)',
    'Budgets invitations, reminders, corrections and completion notices.',
  ),
  emails_per_account_per_day: entry(
    'emails_per_account_per_day',
    200,
    'emails',
    'CALIBRATION',
    'PRD §14.3 (Logical message budgets)',
  ),
  emails_global_per_day: entry(
    'emails_global_per_day',
    5000,
    'emails',
    'CALIBRATION',
    'PRD §14.3 (Logical message budgets)',
  ),
  envelope_creations_per_minute: entry(
    'envelope_creations_per_minute',
    10,
    'creations',
    'CALIBRATION',
    'PRD §14.3 (Envelope creation)',
    'Documented mutation boundary; idempotent retries do not create new requests.',
  ),
  message_max_scalars: entry(
    'message_max_scalars',
    2000,
    'unicode scalar values',
    'CALIBRATION',
    'PRD §14.3 (User-authored text)',
    'Reject or request editing on overflow; never silently truncate.',
  ),
  subject_max_scalars: entry(
    'subject_max_scalars',
    200,
    'unicode scalar values',
    'CALIBRATION',
    'PRD §14.3 (User-authored text)',
  ),
  decline_reason_max_scalars: entry(
    'decline_reason_max_scalars',
    500,
    'unicode scalar values',
    'CALIBRATION',
    'PRD §14.3 (User-authored text)',
  ),

  /* --------------------------------------------------------------------- */
  /* Source validation — calibration defaults (§14.6)                       */
  /* --------------------------------------------------------------------- */
  source_max_bytes: entry(
    'source_max_bytes',
    25 * 1024 * 1024,
    'bytes',
    'CALIBRATION',
    'PRD §14.6 (Source pages/bytes/pixels); §4.6',
    'Calibration default: 25 MiB PDF.',
  ),
  source_max_pages: entry(
    'source_max_pages',
    100,
    'pages',
    'CALIBRATION',
    'PRD §14.6 (Source pages/bytes/pixels); §4.6',
  ),
  image_max_decoded_pixels: entry(
    'image_max_decoded_pixels',
    25_000_000,
    'pixels',
    'CALIBRATION',
    'PRD §14.6 (Source pages/bytes/pixels); §4.6',
    '25 million decoded pixels per image, sequential processing.',
  ),
  import_max_decoded_pixels: entry(
    'import_max_decoded_pixels',
    250_000_000,
    'pixels',
    'CALIBRATION',
    'PRD §14.6 (Source pages/bytes/pixels); §4.6',
    '250 million decoded pixels per import.',
  ),

  /* --------------------------------------------------------------------- */
  /* Cleanup, retention and audit defaults                                  */
  /* --------------------------------------------------------------------- */
  physical_purge_within_hours: entry(
    'physical_purge_within_hours',
    24,
    'hours',
    'CALIBRATION',
    'PRD §14.6 (Cleanup/physical purge); §14.2',
    'Access stops at the logical deadline; purge follows unless a shorter class bound applies.',
  ),
  staging_object_purge_within_hours: entry(
    'staging_object_purge_within_hours',
    1,
    'hours',
    'CALIBRATION',
    'PRD §14.2 (Unreferenced staging objects)',
    'After preparation authority expires; referenced objects and candidates are protected.',
  ),
  identity_lookup_retention_days: entry(
    'identity_lookup_retention_days',
    30,
    'days',
    'REVIEW',
    'PRD §14.2 (Separate operational recipient identity lookup records)',
    'Then delete or irreversibly minimize under the approved policy.',
  ),
  minimized_history_retention_days: entry(
    'minimized_history_retention_days',
    365,
    'days',
    'REVIEW',
    'PRD §14.2 (Minimized envelope and audit history)',
  ),
  financial_retention_years: entry(
    'financial_retention_years',
    7,
    'years',
    'REVIEW',
    'PRD §14.2 (Financial records and deduplication)',
    'Candidate period; jurisdiction/provider review required before paid beta.',
  ),
  diagnostics_retention_days: entry(
    'diagnostics_retention_days',
    14,
    'days',
    'FIXED',
    'PRD §14.2 (Diagnostics and raw tokens); §15.5',
    'Whichever comes first: 14 days or 5 MB. Never persist raw capability tokens.',
  ),
  diagnostics_max_bytes: entry(
    'diagnostics_max_bytes',
    5 * 1024 * 1024,
    'bytes',
    'FIXED',
    'PRD §14.2 (Diagnostics and raw tokens)',
  ),
  redacted_log_retention_days: entry(
    'redacted_log_retention_days',
    7,
    'days',
    'FIXED',
    'PRD §14.2 (Diagnostics and raw tokens)',
  ),
  audit_ip_user_agent_default: entry(
    'audit_ip_user_agent_default',
    false,
    'boolean',
    'REVIEW',
    'PRD §14.6 (Audit metadata/PII); §14.2',
    'Default off; class schedule and privacy review before any collection.',
  ),

  /* --------------------------------------------------------------------- */
  /* Transfer pairing/relay — §14.6                                         */
  /* --------------------------------------------------------------------- */
  transfer_pairing_minutes: entry(
    'transfer_pairing_minutes',
    5,
    'minutes',
    'CALIBRATION',
    'PRD §14.6 (Transfer pairing/relay); §10.7',
  ),
  transfer_session_minutes: entry(
    'transfer_session_minutes',
    30,
    'minutes',
    'CALIBRATION',
    'PRD §14.6 (Transfer pairing/relay)',
  ),
  transfer_max_bytes: entry(
    'transfer_max_bytes',
    1024 * 1024 * 1024,
    'bytes',
    'CALIBRATION',
    'PRD §14.6 (Transfer pairing/relay)',
    '1 GiB streamed transfer maximum.',
  ),
  relay_purge_after_hours: entry(
    'relay_purge_after_hours',
    1,
    'hours',
    'CALIBRATION',
    'PRD §14.6 (Transfer pairing/relay)',
    'Within 1 hour of finish/cancel/timeout.',
  ),

  /* --------------------------------------------------------------------- */
  /* Backup logical layout — §14.6, §8.7                                    */
  /* --------------------------------------------------------------------- */
  backup_provider_model: entry(
    'backup_provider_model',
    'system_file_provider_first',
    'policy',
    'FIXED',
    'PRD §14.6 (Backup provider model); §8.6',
    'Automatic backup requires durable scoped access and safe replacement; manual fallback always available.',
  ),
  backup_logical_directory: entry(
    'backup_logical_directory',
    'SignBento/Backups/<vault-id>',
    'path',
    'FIXED',
    'PRD §14.6 (Backup logical directory); §8.7',
    'current.agbackup with at most one previous snapshot by default; sanitized vault id, not PII.',
  ),
  backup_previous_snapshots: entry(
    'backup_previous_snapshots',
    1,
    'snapshots',
    'CALIBRATION',
    'PRD §14.6 (Backup logical directory); §8.7',
    'Only when the adapter supports safe replacement/rotation.',
  ),

  /* --------------------------------------------------------------------- */
  /* MCP release configuration — §14.6                                      */
  /* --------------------------------------------------------------------- */
  mcp_endpoint: entry(
    'mcp_endpoint',
    'https://mcp.signbento.com/mcp',
    'url',
    'GATE',
    'PRD §14.6 (MCP release configuration)',
    'Freeze issuer/resource identifiers and environment separation before coding.',
  ),
  mcp_review_approval_minutes: entry(
    'mcp_review_approval_minutes',
    5,
    'minutes',
    'FIXED',
    'PRD §14.1; §14.6 (MCP release configuration)',
    'Approval expires after five minutes or any material change, whichever comes first.',
  ),

  /* --------------------------------------------------------------------- */
  /* Performance dataset — beta gate                                        */
  /* --------------------------------------------------------------------- */
  performance_dataset: entry(
    'performance_dataset',
    { local_agreements: 1000, templates: 100 },
    'dataset',
    'GATE',
    'PRD §14.6 (Performance dataset)',
    'Pin devices/OS/browser/network; retain 30-run median/P95 methodology.',
  ),

  /* --------------------------------------------------------------------- */
  /* Workspace resources — §14.3                                             */
  /* --------------------------------------------------------------------- */
  // Storage budgets per plan (Hamza, 28 Oct 2026). Every other workspace bound below stays
  // CALIBRATION until measured.
  free_workspace_account_bytes: entry(
    'free_workspace_account_bytes',
    250 * 1024 * 1024,
    'bytes',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  free_workspace_object_count: entry(
    'free_workspace_object_count',
    100,
    'objects',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  free_workspace_version_count: entry(
    'free_workspace_version_count',
    500,
    'versions',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  lifetime_workspace_account_bytes: entry(
    'lifetime_workspace_account_bytes',
    1024 * 1024 * 1024,
    'bytes',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  lifetime_workspace_object_count: entry(
    'lifetime_workspace_object_count',
    500,
    'objects',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  lifetime_workspace_version_count: entry(
    'lifetime_workspace_version_count',
    2_000,
    'versions',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza 2026-10-28 (DEV-026) ahead of the C10 benchmark and cost model; review after measured use.',
  ),
  workspace_extracted_index_bytes: entry(
    'workspace_extracted_index_bytes',
    null,
    'bytes',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_job_max_pages: entry(
    'workspace_job_max_pages',
    null,
    'pages',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_job_max_bytes: entry(
    'workspace_job_max_bytes',
    null,
    'bytes',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources); §11.8',
    'Input bytes a processing job may account for (the document plus everything decoded from it).',
  ),
  workspace_job_max_pixels: entry(
    'workspace_job_max_pixels',
    null,
    'pixels',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources); §11.8',
    'Declared page geometry a processing job may walk: 1 PDF point = 1 device unit, no DPI claimed.',
  ),
  workspace_job_max_time_ms: entry(
    'workspace_job_max_time_ms',
    null,
    'milliseconds',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_job_max_memory_bytes: entry(
    'workspace_job_max_memory_bytes',
    null,
    'bytes',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_concurrent_jobs: entry(
    'workspace_concurrent_jobs',
    null,
    'jobs',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_query_max_results: entry(
    'workspace_query_max_results',
    null,
    'results',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_query_max_count: entry(
    'workspace_query_max_count',
    null,
    'queries',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_pagination_default: entry(
    'workspace_pagination_default',
    null,
    'items',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_pagination_max: entry(
    'workspace_pagination_max',
    null,
    'items',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_reindex_max_retries: entry(
    'workspace_reindex_max_retries',
    null,
    'retries',
    'CALIBRATION',
    'PRD §14.3 (Workspace resources)',
  ),
  workspace_deletion_grace_hours: entry(
    'workspace_deletion_grace_hours',
    24,
    'hours',
    'FIXED',
    'PRD §14.3 (Workspace resources); C10; DEV-026',
    'Approved by Hamza on 2026-10-28 ("grace yes", DEV-026); the PRD sets no value. Purge within 24 h: the maximum purge deadline. A delete takes effect at once with no undo, and its stored objects are physically purged within this bound (PRD B11).',
  ),

  /* --------------------------------------------------------------------- */
  /* §14.9 V17-CFG-01..11 gate index — supplements §14.6, never renumbers  */
  /* --------------------------------------------------------------------- */
  /* Value `null` means the gated part is still undecided: callers fail     */
  /* closed and no value may be invented (register §4, §10).               */
  v17_cfg_01: entry(
    'v17_cfg_01',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-01)',
    'Decided: Workers / D1-FTS5 / R2 / Queues-Cron, no VPS fallback. Still gated: separate dev/test/prod bindings, secrets, migrations. Owning slices S3.1.',
  ),
  v17_cfg_02: entry(
    'v17_cfg_02',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-02)',
    'Decided: Better Auth selected. Still gated: pinned library/plugin versions, D1 migrations, compatibility flags, session config. Owning slice S3.2 (ADR 0011).',
  ),
  v17_cfg_03: entry(
    'v17_cfg_03',
    {
      /* Approved by Hamza on 2026-10-08 (P17-12, the OTP half): the standard policy. */
      challenge_ttl_seconds: 600,
      max_verify_attempts: 5,
      max_issuances_per_hour: 5,
      resend_after_seconds: 60,
      protected_payload_retention_seconds: 600,
    },
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-03)',
    'Decided: email OTP. Approved 2026-10-08 (Hamza, P17-12 OTP half): challenge TTL 10 min, 5 attempt/rate limits per code, 5 issuances per address per hour, resend generation after 60 s, protected payload retention 10 min. Owning slices S3.2 + S4.1.',
  ),
  v17_cfg_04: entry(
    'v17_cfg_04',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-04)',
    'Decided: Apple/Google + native callbacks + MCP OAuth. Still gated: issuer/resource/registration/PKCE/revocation proof per surface. Owning slices S3.2, SM1.',
  ),
  v17_cfg_05: entry(
    'v17_cfg_05',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-05)',
    'Decided: Cloudflare Email Sending is the only live adapter. Still gated: the SES seam exists with no second active sender. Owning slices S4.1, S4.5.',
  ),
  v17_cfg_06: entry(
    'v17_cfg_06',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-06)',
    'Decided: verified sending domain with SPF/DKIM/DMARC. Still gated: actual account quota, request rate, bounce/suppression, deliverability tests. Owning slices S6.6 (beta gate), S4.1.',
  ),
  v17_cfg_07: entry(
    'v17_cfg_07',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-07)',
    'Unset, so the test environment (and any unnamed one) stays gated: UNKNOWN reconciliation, safe retries, max attempts, priority concurrency, not-after, dedup, dead-letter. The deployed environments read their own approvals, v17_cfg_07_dev and v17_cfg_07_production. Owning slice S4.1.',
  ),
  v17_cfg_07_dev: entry(
    'v17_cfg_07_dev',
    {
      /* Dev only, approved 2026-10-12 by the advisor for Hamza; production pending (v17_cfg_07).
       * The values are the attested test policy (packages/mail/src/test-helpers.ts ATTESTED_POLICY).
       * The concurrency matches each dev queue consumer's max_concurrency in services/worker/wrangler.jsonc
       * (priority 4, bulk 2). */
      max_attempts: 3,
      // Only a REJECTED outcome the provider itself marks retryable; UNKNOWN is never replayed blind.
      safe_retry: ['REJECTED'],
      priority_concurrency: { AUTH_SERVICE: 4, SIGNING_STATUS: 2, BULK: 2 },
      not_after_behaviour: 'SKIP',
      unknown_reconciliation: 'CORRELATION_ONLY',
      dead_letter_recovery: 'MANUAL',
      backoff_seconds: 60,
    },
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-07)',
    'Dev only: approved 2026-10-12 by the advisor for Hamza, read only where SBN_ENVIRONMENT is dev. Production reads v17_cfg_07_production. Owning slice S4.1.',
  ),
  v17_cfg_07_production: entry(
    'v17_cfg_07_production',
    {
      /* Production, approved 2026-10-15 by Hamza (via the advisor): the same values as dev
       * (v17_cfg_07_dev). The concurrency matches each production queue consumer's max_concurrency
       * in services/worker/wrangler.jsonc (priority 4, bulk 2). */
      max_attempts: 3,
      // Only a REJECTED outcome the provider itself marks retryable; UNKNOWN is never replayed blind.
      safe_retry: ['REJECTED'],
      priority_concurrency: { AUTH_SERVICE: 4, SIGNING_STATUS: 2, BULK: 2 },
      not_after_behaviour: 'SKIP',
      unknown_reconciliation: 'CORRELATION_ONLY',
      dead_letter_recovery: 'MANUAL',
      backoff_seconds: 60,
    },
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-07)',
    'Production: approved 2026-10-15 by Hamza (via the advisor), the dev values; read only where SBN_ENVIRONMENT is production. The test environment still reads the unset v17_cfg_07. Owning slice S4.1.',
  ),
  v17_cfg_08: entry(
    'v17_cfg_08',
    {
      /* Unset until approved; readers must fail closed rather than invent a retention.
       * OTP verifier storage approved 2026-10-08 (Hamza): hashed, the only form the Worker supports. */
      otp_verifier_storage: 'hashed',
      outbox_payload_ttl_seconds: null,
      provider_event_retention_days: null,
      suppression_retention_days: null,
    },
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-08)',
    'Approved 2026-10-08: OTP verifier storage = hashed. Still gated: outbox payload TTL, provider-event/suppression retention, erasure. Owning slices S0.10, S4.1. The open members are deliberately null.',
  ),
  v17_cfg_09: entry(
    'v17_cfg_09',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-09)',
    'Still gated: document-processing benchmark against Workers limits; an isolated runtime only if approved. Owning slice SM2W.',
  ),
  v17_cfg_10: entry(
    'v17_cfg_10',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-10)',
    'Decided: §15.8 cutover procedure. Still gated: SES implemented and certified only when required. Owning slices S4.5 (seam now), S6.6.',
  ),
  v17_cfg_11: entry(
    'v17_cfg_11',
    null,
    'gate',
    'GATE',
    'PRD §14.9 (V17-CFG-11)',
    'Decided: Addenda C/D adopted (2026-09-23). Still gated: the §8/§10 profiles, limits and targets below must be frozen with measured values before beta. Owning slices S0.11, S6.4, S6.6.',
  ),

  /* --------------------------------------------------------------------- */
  /* Adopted Addendum C/D defaults, limits and targets — register §8        */
  /* Approved values, not achieved results: GATE/CALIBRATION until measured */
  /* --------------------------------------------------------------------- */
  text_field_max_scalars: entry(
    'text_field_max_scalars',
    4000,
    'unicode scalar values',
    'CALIBRATION',
    'PRD Part Three D7',
    'Sender may set a lower limit; overflow is detected before activation, never silently clipped.',
  ),
  tooltip_max_scalars: entry(
    'tooltip_max_scalars',
    500,
    'unicode scalar values',
    'CALIBRATION',
    'PRD Part Three D7',
  ),
  category_default: entry(
    'category_default',
    'Uncategorized',
    'label',
    'CALIBRATION',
    'PRD Part Three D5',
    'Optional category defaulting to "Uncategorized".',
  ),
  category_suggested_values: entry(
    'category_suggested_values',
    ['NDA', 'Service Agreement', 'Statement of Work', 'Consent/Waiver', 'Other'],
    'labels',
    'CALIBRATION',
    'PRD Part Three D5',
    'Suggested values only; the field stays free text.',
  ),
  validation_presets: entry(
    'validation_presets',
    ['None', 'Email', 'Numbers', 'Letters', 'Date', 'ZIP', 'ZIP+4', 'SSN', 'Custom'],
    'presets',
    'CALIBRATION',
    'PRD Part Three D7',
    'Closed versioned enum; the profile version and per-preset rules live in @signbento/contracts. SSN is format-only and never leaves the device.',
  ),
  custom_pattern_max_length: entry(
    'custom_pattern_max_length',
    null,
    'unicode scalar values',
    'GATE',
    'PRD Part Three D7',
    'Bounded safe pattern engine. The bound is not frozen yet (register §10 profile freeze); callers fail closed.',
  ),
  formula_operations: entry(
    'formula_operations',
    [
      'ADD',
      'SUBTRACT',
      'MULTIPLY',
      'DIVIDE',
      'PARENTHESES',
      'DECIMAL_CONSTANTS',
      'FIELD_REFERENCES',
      'SUM',
      'MIN',
      'MAX',
      'ROUND',
    ],
    'operations',
    'CALIBRATION',
    'PRD Part Three D9',
    'Restricted local parser; never eval or JavaScript.',
  ),
  formula_dependency_rule: entry(
    'formula_dependency_rule',
    ['SAME_SIGNER_FIELD', 'SENDER_PREFILLED_IMMUTABLE'],
    'dependency kinds',
    'CALIBRATION',
    'PRD Part Three D9',
    'Adopted restriction: cross-signer calculations are excluded.',
  ),
  formula_rounding_policy_version: entry(
    'formula_rounding_policy_version',
    null,
    'version',
    'GATE',
    'PRD Part Three D9',
    'The decimal/rounding profile is not frozen yet (register §10); contracts require the version field, this row holds its value.',
  ),
  anchor_normalization_profile_version: entry(
    'anchor_normalization_profile_version',
    null,
    'version',
    'GATE',
    'PRD Part Three D8',
    'The anchor matching/normalization profile is not frozen yet (register §10).',
  ),
  attachment_tier: entry(
    'attachment_tier',
    'LIFETIME',
    'tier',
    'CALIBRATION',
    'PRD Part Three D10',
    'Recipient attachment collection is Lifetime only.',
  ),
  attachment_allowed_types: entry(
    'attachment_allowed_types',
    ['PDF', 'JPEG', 'PNG'],
    'types',
    'CALIBRATION',
    'PRD Part Three D10',
  ),
  attachment_max_files_per_field: entry(
    'attachment_max_files_per_field',
    3,
    'files',
    'CALIBRATION',
    'PRD Part Three D10',
    'Validate against the cost model before beta.',
  ),
  attachment_max_file_bytes: entry(
    'attachment_max_file_bytes',
    5 * 1024 * 1024,
    'bytes',
    'CALIBRATION',
    'PRD Part Three D10',
    'Per-file limit; validate against the cost model before beta.',
  ),
  attachment_max_total_bytes: entry(
    'attachment_max_total_bytes',
    20 * 1024 * 1024,
    'bytes',
    'CALIBRATION',
    'PRD Part Three D10',
    'Per-envelope limit; one managed envelope still consumes one credit regardless of attachments.',
  ),
  attachment_default_viewers: entry(
    'attachment_default_viewers',
    ['UPLOADER', 'SENDER'],
    'viewers',
    'CALIBRATION',
    'PRD Part Three D10',
    'Default visibility is uploader + sender.',
  ),
  attachment_finalizer_access: entry(
    'attachment_finalizer_access',
    'reviewed_key_access_contract',
    'policy',
    'CALIBRATION',
    'PRD Part Three D10',
    'Finalizer access follows only the reviewed key-access contract; attachments are never auto-appended to the signed PDF.',
  ),

  /* C8 performance targets — approved, not measured (register §8) */
  perf_local_tap_edit_p95_ms: entry(
    'perf_local_tap_edit_p95_ms',
    100,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: local tap/edit feedback. Target — not measured.',
  ),
  perf_local_autosave_p95_ms: entry(
    'perf_local_autosave_p95_ms',
    500,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: debounce end → durable commit. Target — not measured.',
  ),
  perf_local_search_p95_ms: entry(
    'perf_local_search_p95_ms',
    300,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: across 10,000 metadata records. Target — not measured.',
  ),
  perf_ordinary_pdf_first_page_p95_ms: entry(
    'perf_ordinary_pdf_first_page_p95_ms',
    2000,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: 1–5 MB fixtures, after bytes are local. Target — not measured.',
  ),
  perf_mcp_metadata_status_p95_ms: entry(
    'perf_mcp_metadata_status_p95_ms',
    750,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: server ingress → response. Target — not measured.',
  ),
  perf_workspace_fulltext_search_p95_ms: entry(
    'perf_workspace_fulltext_search_p95_ms',
    1500,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: warm index, 1,000 published documents, first page. Target — not measured.',
  ),
  perf_activation_commit_p95_ms: entry(
    'perf_activation_commit_p95_ms',
    1500,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: approval + uploads complete → durable commit. Target — not measured.',
  ),
  perf_text_extraction_indexing_p95_ms: entry(
    'perf_text_extraction_indexing_p95_ms',
    10000,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: queue wait + processing, 20-page text PDF; OCR measured separately. Target — not measured.',
  ),
  perf_client_finalization_p95_ms: entry(
    'perf_client_finalization_p95_ms',
    5000,
    'milliseconds',
    'GATE',
    'PRD Part Three C8',
    'Boundary: 20-page/five-signer fixture, inputs local, excludes network. Target — not measured.',
  ),
  perf_benchmark_recording: entry(
    'perf_benchmark_recording',
    ['p99', 'failure_rate', 'peak_memory', 'transferred_bytes', 'processing_cost', 'queue_delay'],
    'metrics',
    'GATE',
    'PRD Part Three C8',
    'Recorded per benchmark. Failures and timeouts are never excluded from the reported percentiles.',
  ),

  /* C12 evaluation thresholds — targets, not results */
  eval_min_distinct_tasks_per_host: entry(
    'eval_min_distinct_tasks_per_host',
    100,
    'tasks',
    'GATE',
    'PRD Part Three C12',
    'Per certified host; the evaluation set is frozen before certification (P17-09).',
  ),
  eval_success_rate_target_percent: entry(
    'eval_success_rate_target_percent',
    95,
    'percent',
    'GATE',
    'PRD Part Three C12',
    'Success on the frozen eligible agent-task set; no denominator gaming.',
  ),
  eval_p0_deterministic_pass_percent: entry(
    'eval_p0_deterministic_pass_percent',
    100,
    'percent',
    'GATE',
    'PRD Part Three C12',
    'Mandatory deterministic P0 tests must all pass.',
  ),
  eval_tolerance_unauthorized_disclosures: entry(
    'eval_tolerance_unauthorized_disclosures',
    0,
    'violations',
    'GATE',
    'PRD Part Three C12',
    'Zero observed unauthorized disclosures, consent bypasses or duplicate financial effects.',
  ),

  /* C10 / §15.1 cost model — measurement requirement */
  cost_model_components: entry(
    'cost_model_components',
    [
      'storage',
      'database_index',
      'processing',
      'requests',
      'egress',
      'delivery',
      'monitoring',
      'allocated_shared_infrastructure',
    ],
    'components',
    'GATE',
    'PRD Part Three C10; §15.1',
    'Measurement requirement, not an achieved result.',
  ),
  /* §15.1 rate card — configurable, rechecked before launch (OPS-03). */
  cost_rate_r2_gb_month_usd: entry(
    'cost_rate_r2_gb_month_usd',
    0.015,
    'usd per gb-month',
    'GATE',
    'PRD §10.3; §15.1 (R2 Standard rate card)',
    'Before allowances and operations. Recheck before launch; the estimate is not a bill.',
  ),
  cost_rate_workers_month_minimum_usd: entry(
    'cost_rate_workers_month_minimum_usd',
    5,
    'usd per month',
    'GATE',
    'PRD §10.3; §15.1 (Workers Paid minimum)',
    'Usage is additional to the plan minimum.',
  ),
  cost_rate_email_included_per_account_month: entry(
    'cost_rate_email_included_per_account_month',
    3000,
    'emails',
    'GATE',
    'PRD §10.3; §15.1 (Cloudflare Email allowance)',
    'Shared account-wide across the auth, service and managed lanes.',
  ),
  cost_rate_email_per_1000_usd: entry(
    'cost_rate_email_per_1000_usd',
    0.35,
    'usd per 1000 emails',
    'GATE',
    'PRD §10.3; §15.1 (Cloudflare Email overage)',
    'Billable emails beyond the included allowance.',
  ),
  cost_model_cohorts: entry(
    'cost_model_cohorts',
    [
      'free_local_only',
      'free_remote_signing',
      'free_workspace',
      'lifetime_manual',
      'lifetime_managed',
    ],
    'cohorts',
    'GATE',
    'PRD Part Three C10; §15.1',
    'Monthly service cost is reported per cohort.',
  ),
} as const satisfies Record<string, ReleaseConfigEntry>

export type ReleaseConfigKey = keyof typeof RELEASE_CONFIG
export type ReleaseConfig = typeof RELEASE_CONFIG

/**
 * §14.9 gate index — the eleven `V17-CFG` rows in order (register §4).
 * One place for later slices to enumerate and record gate decisions.
 */
export const V17_GATE_KEYS = [
  'v17_cfg_01',
  'v17_cfg_02',
  'v17_cfg_03',
  'v17_cfg_04',
  'v17_cfg_05',
  'v17_cfg_06',
  'v17_cfg_07',
  'v17_cfg_08',
  'v17_cfg_09',
  'v17_cfg_10',
  'v17_cfg_11',
] as const satisfies readonly ReleaseConfigKey[]

/**
 * A gate value is unset when it is `null`, or when it is a structured gate whose every
 * member is still `null` (the shape is published so readers can fail closed per key
 * without inventing a value).
 */
export function isUnsetValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value !== 'object' || Array.isArray(value)) return false
  const members = Object.values(value as Record<string, unknown>)
  return members.length > 0 && members.every((member) => member === null)
}

/** Entries that still need a recorded calibration/review result. */
export function unsetCalibrationKeys(): ReleaseConfigKey[] {
  return (Object.keys(RELEASE_CONFIG) as ReleaseConfigKey[]).filter((key) =>
    isUnsetValue(RELEASE_CONFIG[key].value),
  )
}

export function isCalibrated(key: ReleaseConfigKey): boolean {
  return !isUnsetValue(RELEASE_CONFIG[key].value)
}

/**
 * Reads a structured gate's published shape. Returns `null` while the whole gate is
 * unset, and otherwise the member map so a caller can name the member it needs.
 */
export function gateMembers(key: ReleaseConfigKey): Record<string, unknown> | null {
  const value = RELEASE_CONFIG[key].value
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

/**
 * Reads one member of a structured gate. Throws while it is unset so callers fail
 * closed instead of falling back to a default.
 */
export function requireGateMember<T>(key: ReleaseConfigKey, member: string): T {
  const members = gateMembers(key)
  const value = members?.[member]
  if (value === null || value === undefined) {
    throw new Error(`${key}.${member} is a GATE value and has not been approved yet`)
  }
  return value as T
}

/**
 * Read a calibration-gated entry. Throws while unset: a caller that needs a
 * workspace budget must fail closed rather than fall back to unlimited.
 */
export function requireCalibrated<T>(key: ReleaseConfigKey): T {
  const value = RELEASE_CONFIG[key].value
  if (isUnsetValue(value)) {
    throw new Error(`Release configuration "${key}" is not calibrated yet (§14.3)`)
  }
  return value as T
}

/** All register entries with their keys — used by readiness dashboards and tests. */
export function releaseConfigEntries(): ReadonlyArray<
  readonly [ReleaseConfigKey, ReleaseConfigEntry]
> {
  return Object.entries(RELEASE_CONFIG) as ReadonlyArray<
    readonly [ReleaseConfigKey, ReleaseConfigEntry]
  >
}
